import React, { useEffect, useRef, useState } from 'react'
import { SubmitErrorHandler } from 'react-hook-form'
import { useHistory, Link } from 'react-router-dom'
import isEqual from 'lodash/isEqual'
import { InlineMessage, INLINE_MESSAGE_VARIANTS } from 'transfix-ui/components/InlineMessage'
import { Box } from 'transfix-ui/core'
import { StringParam } from 'use-query-params'
import { useQueryParams } from 'utils/hooks/useQueryParams'
import {
  ReturningUserForm,
  ReturningUserSchemaType,
} from 'apps/fleet-planner/components/forms/ReturningUserForm'
import { getRoute } from 'apps/fleet-planner/constants'
import { useUserContext } from 'apps/fleet-planner/context'
import { useUserAuth, useFMSAnalytics } from 'apps/fleet-planner/hooks'
import { OnboardingStepContainer } from './OnboardingStepContainer'

export const OnboardingStepReturningUser = () => {
  const { trackEvent } = useFMSAnalytics()
  const history = useHistory()
  const [{ token }] = useQueryParams({
    token: StringParam,
  })
  const { getUser, updateUserInfo } = useUserContext()
  const { handleReturningUser } = useUserAuth()

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string | undefined>()
  const [loginErrorMessage, setLoginErrorMessage] = useState<string | undefined>()

  const user = getUser()
  const { cellPhone = '', firstName = '', id: userId = '', lastName = '', phone = '' } = user ?? {}

  useEffect(() => {
    trackEvent('Onboarding', 'ONBOARDING_VIEWED_CONFIRM_YOUR_ACCOUNT_INFO_PAGE')
  }, [trackEvent])

  /**
   * When this component mounts, log in the user with token param
   */
  const hasTriggeredLogin = useRef(false)
  useEffect(() => {
    async function login() {
      try {
        setIsLoading(true)
        setLoginErrorMessage(undefined)
        await handleReturningUser(token)
      } catch (e) {
        trackEvent('Onboarding', 'ONBOARDING_AUTH_FAILED', e)
        setLoginErrorMessage(e)
      } finally {
        setIsLoading(false)
      }
    }

    if (!hasTriggeredLogin.current) {
      login()
      hasTriggeredLogin.current = true
    }
  }, [handleReturningUser, token, trackEvent])

  /**
   * Trigger PUT request only if there are updated values.
   * Disabled if request is already in progress.
   */
  const updateUserHandler = async (data: ReturningUserSchemaType): Promise<void> => {
    if (isLoading) return

    const hasNewValues = !isEqual({ firstName, lastName, phone, cellPhone }, data)
    if (hasNewValues) {
      setIsLoading(true)
      try {
        await updateUserInfo({
          user: {
            firstName: data?.firstName,
            lastName: data?.lastName,
            phone: data?.phone,
            cellPhone: data?.cellPhone,
          },
        })
        setIsLoading(false)
        setErrorMessage(undefined)
        trackEvent(
          'Onboarding',
          'ONBOARDING_SUCCESSFULLY_SUBMITTED_FORM_ON_CONFIRM_YOUR_ACCOUNT_INFO_PAGE',
          data
        )
        history.push(getRoute('options'))
      } catch (e) {
        trackEvent('Onboarding', 'ONBOARDING_FORM_API_ERROR_RETURNING_USER_PAGE', e)
        setErrorMessage(e)
        setIsLoading(false)
      }
    } else {
      history.push(getRoute('options'))
    }
  }

  const errorHandler: SubmitErrorHandler<ReturningUserSchemaType> = errors => {
    trackEvent(
      'Onboarding',
      'ONBOARDING_FAILED_TO_SUBMIT_FORM_ON_CONFIRM_YOUR_ACCOUNT_INFO_PAGE',
      errors
    )
  }

  const hasValidAuth = Boolean(token && userId && !loginErrorMessage)

  return (
    <OnboardingStepContainer
      title='Does this info look right?'
      subtitle='This is the name and contact info we have from your existing Transfix account.'
      fadeIn
      maxWidth={424}>
      {hasValidAuth ? (
        <ReturningUserForm
          onSuccess={updateUserHandler}
          onError={errorHandler}
          defaultValues={{
            firstName,
            lastName,
            phone,
            cellPhone,
          }}
        />
      ) : (
        <Box>
          <InlineMessage
            fullWidth
            type={isLoading ? INLINE_MESSAGE_VARIANTS.INFO : INLINE_MESSAGE_VARIANTS.ALERT}
            message={
              isLoading
                ? 'Loading...'
                : errorMessage || 'There was an error with your verification link.'
            }
          />
          <Box mt={2}>
            <Link to={getRoute('emailInput')}>Start over</Link>
          </Box>
        </Box>
      )}
    </OnboardingStepContainer>
  )
}
